import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { API_CONFIG } from '../../config/apiRouting';
import './ReasonCodesManagement.css';

const API_BASE = API_CONFIG.gateway.baseUrl;

interface ReasonCode {
  reasonCodeId: string;
  enterpriseId: string;
  code: string;
  description: string;
  usageAreas: string[];
  requiresApproval: boolean;
  requiresComment: boolean;
  active: boolean;
  createdAt: string;
  updatedAt: string;
  createdBy: string;
  metadata?: any;
}

interface UsageArea {
  value: string;
  label: string;
  description: string;
}

interface ReasonCodesManagementProps {
  enterpriseId: string;
  onClose?: () => void;
}

const ReasonCodesManagement: React.FC<ReasonCodesManagementProps> = ({ 
  enterpriseId, 
  onClose 
}) => {
  const { getIdToken } = useAuth();
  const [reasonCodes, setReasonCodes] = useState<ReasonCode[]>([]);
  const [usageAreas, setUsageAreas] = useState<UsageArea[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingCode, setEditingCode] = useState<ReasonCode | null>(null);
  
  // Form state
  const [formData, setFormData] = useState({
    code: '',
    description: '',
    usageAreas: [] as string[],
    requiresApproval: false,
    requiresComment: false,
    active: true
  });

  useEffect(() => {
    loadUsageAreas();
    loadReasonCodes();
  }, [enterpriseId]);

  const loadUsageAreas = async () => {
    try {
      const response = await fetch(`${API_BASE}/api/reason-codes/usage-areas`);
      const data = await response.json();
      
      if (data.success) {
        setUsageAreas(data.data);
      }
    } catch (err) {
      console.error('Error loading usage areas:', err);
    }
  };

  const loadReasonCodes = async () => {
    if (!enterpriseId) return;
    
    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const response = await fetch(
        `${API_BASE}/api/reason-codes/${enterpriseId}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        setReasonCodes(data.data);
      } else {
        setError(data.error || 'Failed to load reason codes');
      }
    } catch (err) {
      console.error('Error loading reason codes:', err);
      setError('Failed to load reason codes');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.code || !formData.description || formData.usageAreas.length === 0) {
      setError('Please fill in all required fields and select at least one usage area');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const isEditing = !!editingCode;
      
      const url = isEditing
        ? `${API_BASE}/api/reason-codes/${enterpriseId}/${editingCode.reasonCodeId}`
        : `${API_BASE}/api/reason-codes/${enterpriseId}`;
      
      const response = await fetch(url, {
        method: isEditing ? 'PUT' : 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(formData)
      });

      const data = await response.json();
      
      if (data.success) {
        await loadReasonCodes();
        resetForm();
        setShowAddForm(false);
        setEditingCode(null);
      } else {
        setError(data.error || 'Failed to save reason code');
      }
    } catch (err) {
      console.error('Error saving reason code:', err);
      setError('Failed to save reason code');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (reasonCode: ReasonCode) => {
    setEditingCode(reasonCode);
    setFormData({
      code: reasonCode.code,
      description: reasonCode.description,
      usageAreas: reasonCode.usageAreas,
      requiresApproval: reasonCode.requiresApproval,
      requiresComment: reasonCode.requiresComment,
      active: reasonCode.active
    });
    setShowAddForm(true);
  };

  const handleDelete = async (reasonCodeId: string) => {
    if (!window.confirm('Are you sure you want to delete this reason code?')) {
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const token = await getIdToken();
      const response = await fetch(
        `${API_BASE}/api/reason-codes/${enterpriseId}/${reasonCodeId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`
          }
        }
      );

      const data = await response.json();
      
      if (data.success) {
        await loadReasonCodes();
      } else {
        setError(data.error || 'Failed to delete reason code');
      }
    } catch (err) {
      console.error('Error deleting reason code:', err);
      setError('Failed to delete reason code');
    } finally {
      setLoading(false);
    }
  };

  const toggleUsageArea = (area: string) => {
    setFormData(prev => ({
      ...prev,
      usageAreas: prev.usageAreas.includes(area)
        ? prev.usageAreas.filter(a => a !== area)
        : [...prev.usageAreas, area]
    }));
  };

  const resetForm = () => {
    setFormData({
      code: '',
      description: '',
      usageAreas: [],
      requiresApproval: false,
      requiresComment: false,
      active: true
    });
    setEditingCode(null);
  };

  const getUsageAreaLabel = (value: string): string => {
    const area = usageAreas.find(a => a.value === value);
    return area?.label || value;
  };

  if (loading && reasonCodes.length === 0) {
    return (
      <div className="reason-codes-management loading">
        <div className="loading-spinner">Loading reason codes...</div>
      </div>
    );
  }

  return (
    <div className="reason-codes-management">
      <div className="reason-codes-header">
        <h3>Reason Codes Management</h3>
        <div className="header-actions">
          <button
            type="button"
            className="add-button"
            onClick={() => {
              resetForm();
              setShowAddForm(true);
            }}
            disabled={loading}
          >
            + Add Reason Code
          </button>
          {onClose && (
            <button
              type="button"
              className="close-button"
              onClick={onClose}
            >
              Close
            </button>
          )}
        </div>
      </div>

      {error && (
        <div className="error-message">
          <span className="error-icon">ERROR:</span>
          {error}
        </div>
      )}

      {showAddForm && (
        <div className="reason-code-form">
          <h4>{editingCode ? 'Edit Reason Code' : 'Add New Reason Code'}</h4>
          <form onSubmit={handleSubmit}>
            <div className="form-row">
              <div className="form-group">
                <label>Code *</label>
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData({ ...formData, code: e.target.value.toUpperCase() })}
                  placeholder="e.g., VOID01, COMP02"
                  maxLength={20}
                  required
                />
              </div>

              <div className="form-group">
                <label>Description *</label>
                <input
                  type="text"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="e.g., Manager void - incorrect order"
                  required
                />
              </div>
            </div>

            <div className="form-group">
              <label>Usage Areas * (Select all that apply)</label>
              <div className="usage-areas-grid">
                {usageAreas.map(area => (
                  <div
                    key={area.value}
                    className={`usage-area-option ${formData.usageAreas.includes(area.value) ? 'selected' : ''}`}
                    onClick={() => toggleUsageArea(area.value)}
                    title={area.description}
                  >
                    <span className="checkbox">
                      {formData.usageAreas.includes(area.value) ? '[X]' : '[ ]'}
                    </span>
                    <span className="label">{area.label}</span>
                  </div>
                ))}
              </div>
            </div>

            <div className="form-row">
              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.requiresApproval}
                    onChange={(e) => setFormData({ ...formData, requiresApproval: e.target.checked })}
                  />
                  Requires Manager Approval
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.requiresComment}
                    onChange={(e) => setFormData({ ...formData, requiresComment: e.target.checked })}
                  />
                  Requires Comment
                </label>
              </div>

              <div className="form-group checkbox-group">
                <label>
                  <input
                    type="checkbox"
                    checked={formData.active}
                    onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                  />
                  Active
                </label>
              </div>
            </div>

            <div className="form-actions">
              <button
                type="submit"
                className="save-button"
                disabled={loading}
              >
                {loading ? 'Saving...' : (editingCode ? 'Update' : 'Save')}
              </button>
              <button
                type="button"
                className="cancel-button"
                onClick={() => {
                  resetForm();
                  setShowAddForm(false);
                }}
                disabled={loading}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      <div className="reason-codes-list">
        <h4>Existing Reason Codes ({reasonCodes.length})</h4>
        {reasonCodes.length === 0 ? (
          <div className="empty-state">
            <p>No reason codes configured yet.</p>
            <p>Add reason codes to track voids, returns, adjustments, and other POS operations.</p>
          </div>
        ) : (
          <div className="codes-table">
            <table>
              <thead>
                <tr>
                  <th>Code</th>
                  <th>Description</th>
                  <th>Usage Areas</th>
                  <th>Options</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {reasonCodes.map(code => (
                  <tr key={code.reasonCodeId} className={!code.active ? 'inactive' : ''}>
                    <td className="code-cell">
                      <strong>{code.code}</strong>
                    </td>
                    <td>{code.description}</td>
                    <td className="usage-areas-cell">
                      <div className="usage-area-tags">
                        {code.usageAreas.map(area => (
                          <span key={area} className="usage-tag">
                            {getUsageAreaLabel(area)}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="options-cell">
                      {code.requiresApproval && (
                        <span className="option-badge approval">Approval Required</span>
                      )}
                      {code.requiresComment && (
                        <span className="option-badge comment">Comment Required</span>
                      )}
                    </td>
                    <td>
                      <span className={`status-badge ${code.active ? 'active' : 'inactive'}`}>
                        {code.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td className="actions-cell">
                      <button
                        className="edit-button"
                        onClick={() => handleEdit(code)}
                        disabled={loading}
                      >
                        Edit
                      </button>
                      <button
                        className="delete-button"
                        onClick={() => handleDelete(code.reasonCodeId)}
                        disabled={loading}
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default ReasonCodesManagement;